import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Link,
  CommonAuthButton,
  SliderWrapper,
  EmailInput,
  CommonAuthFields,
  PasswordInput,
} from "../index";
import styles from "./NewLogin.module.css";
import { NEW_ONBOARDING_ROUTES } from "../constants/routes.constants";
import { useUTMToLocalStorage } from "../../../shared/hooks";
import { validateEmail } from "../utils/passwordUtils";
import { GoAlert } from "react-icons/go";
import GreenCheck from "../../../assets/icons/typcn_tick.svg";
import Logo from "../../../assets/img/skillgigs.png";
import { Spin } from "antd";
import { setCookie } from "../../../shared/helpers";
import { useHistory } from "react-router-dom";
import { passwordlessStartApi } from "../../../API/API";
import { Auth } from "../../../config/aws-exports";
import { updateUserInfo } from "../../../redux/actions";

const PASSWORD_LOGIN_DOMAIN_NAMES = [
  "shi",
  "iinn",
  "favordelivery",
  "heb",
  "presidio",
  "aspirehospital",
  "skillgigs",
  "ugspk",
];

const isPasswordLoginEmail = (value) => {
  const domainName = value.toLowerCase().match(/@([^.\s@]+)\./)?.[1];
  return PASSWORD_LOGIN_DOMAIN_NAMES.includes(domainName);
};

export const NewLogin = () => {
  useUTMToLocalStorage();
  const history = useHistory();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [greenCheck, setGreenCheck] = useState(false);
  const [showCheckIconEmail, setShowCheckIconEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const validateEmailLive = (e) => {
    const value = e.target.value.toLowerCase();
    setEmail(value);
    setErrorMsg("");

    if (!isPasswordLoginEmail(value)) {
      setPassword("");
    }

    if (value.length > 0) {
      setShowCheckIconEmail(true);
    } else {
      setShowCheckIconEmail(false);
    }

    setGreenCheck(validateEmail(value));
  };

  const handleSocialClick = () => {};
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setErrorMsg("");
  };

  const isEmailDisabled = loading || !email || !validateEmail(email);
  const shouldLoginWithPassword = isPasswordLoginEmail(email);
  const isPasswordLoginDisabled = isEmailDisabled || !password;

  const handleRequestCode = async () => {
    if (!validateEmail(email)) {
      setErrorMsg("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const normalizedEmail = email.toLowerCase();

      localStorage.setItem("Email", normalizedEmail);

      const response = await passwordlessStartApi({
        email: normalizedEmail,
        purpose: "login",
      });
      const data = response?.data || {};

      if (data.token || data.idToken) {
        setCookie("token", data.token || data.idToken, 30);
        localStorage.setItem("emailVerified", "true");
        window.location.replace("/employer/auth");
        return;
      }

      history.push("/verify-email", {
        email: normalizedEmail,
        fromLogin: true,
        challengeSession: data.session,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "We could not start email sign-in. Please try again.";
      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (isPasswordLoginDisabled) return;

    setLoading(true);
    setErrorMsg("");

    try {
      const normalizedEmail = email.toLowerCase();
      const user = await Auth.signIn({
        username: normalizedEmail,
        password,
      });

      if (user?.challengeName === "NEW_PASSWORD_REQUIRED") {
        history.push(
          `/force-password-change?email=${encodeURIComponent(normalizedEmail)}`
        );
        return;
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      setCookie("token", token);
      dispatch(updateUserInfo(token));
      history.push("/auth");
    } catch (error) {
      const message =
        error?.message || "We could not sign you in. Please try again.";
      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (shouldLoginWithPassword) {
      handlePasswordLogin();
      return;
    }
    handleRequestCode();
  };

  return (
    <SliderWrapper>
      <div className={styles.formCard}>
        <div className={styles.brandWrap}>
          <img src={Logo} alt="SkillGigs" className={styles.brandLogo} />
        </div>

        <h2 className={styles.title}>Login in your account</h2>

        <form className={styles.form} onSubmit={handleSubmit}>
          <CommonAuthFields
            handleSocialClick={handleSocialClick}
            isSignup={false}
          />

          <div
            className={
              !emailFocused
                ? greenCheck
                  ? styles.validInput
                  : showCheckIconEmail
                    ? styles.invalidInput
                    : ""
                : ""
            }
          >
            <EmailInput
              autoFocus
              required
              label="Email Address"
              value={email}
              handleChange={validateEmailLive}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              placeholder="Enter the email address"
              suffix={
                greenCheck ? (
                  <img src={GreenCheck} alt="valid" />
                ) : showCheckIconEmail && !emailFocused ? (
                  <GoAlert style={{ color: "#db2828" }} />
                ) : null
              }
            />
          </div>

          {showCheckIconEmail &&
            !greenCheck &&
            !emailFocused &&
            email?.length > 0 && (
              <p className={styles.errorText}>
                Looks like this email is incomplete
              </p>
            )}

          {errorMsg && <p className={styles.errorText}>{errorMsg}</p>}

          <div
            className={`${styles.passwordFieldWrap} ${
              shouldLoginWithPassword ? styles.passwordFieldVisible : ""
            }`}
            aria-hidden={!shouldLoginWithPassword}
          >
            <PasswordInput
              required={shouldLoginWithPassword}
              disabled={!shouldLoginWithPassword}
              value={password}
              handleChange={handlePasswordChange}
              autoComplete="current-password"
            />
          </div>

          <CommonAuthButton
            type="submit"
            className={styles.submitBtn}
            disabled={
              shouldLoginWithPassword
                ? isPasswordLoginDisabled
                : isEmailDisabled
            }
            loading={loading}
            label={
              loading ? (
                <Spin size="small" />
              ) : shouldLoginWithPassword ? (
                "Login"
              ) : (
                "Verify email"
              )
            }
          />

          {shouldLoginWithPassword && (
            <p className={styles.bottomText}>
              Forgot password?{" "}
              <Link
                to={NEW_ONBOARDING_ROUTES.FORGOT_PASSWORD}
                className={styles.bottomLink}
              >
                Reset it
              </Link>
            </p>
          )}

          <p className={styles.bottomText}>
            Don't have an account?{" "}
            <Link
              to={NEW_ONBOARDING_ROUTES.SIGNUP}
              className={styles.bottomLink}
            >
              Sign up
            </Link>
          </p>
        </form>
      </div>
    </SliderWrapper>
  );
};
